/**
 * wsUi — palette + formatters shared by the Options Workspace panels.
 * Colours come from the app theme tokens so light and dark both work.
 */
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAppTheme } from '../../../hooks/useAppTheme';
import { USD_TO_INR } from '../../../utils/constants';

export type Ccy = 'INR' | 'USD';

export function useWsColors() {
  const { C, isDark } = useAppTheme();
  const theme = useTheme();
  return useMemo(() => ({
    isDark,
    bg: theme.palette.background.default,
    panel: theme.palette.background.paper,
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)',
    borderStrong: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.18)',
    text: theme.palette.text.primary,
    dim: theme.palette.text.secondary,
    faint: isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)',
    green: isDark ? '#26c281' : '#0b7a4b',
    red: isDark ? '#f0616d' : '#c62828',
    greenBg: isDark ? 'rgba(38,194,129,0.14)' : 'rgba(11,122,75,0.10)',
    redBg: isDark ? 'rgba(240,97,109,0.14)' : 'rgba(198,40,40,0.09)',
    amber: isDark ? '#f5b041' : '#b45309',
    amberBg: isDark ? 'rgba(245,176,65,0.14)' : 'rgba(180,83,9,0.10)',
    blue: isDark ? '#7aa7ff' : '#1d4ed8',
    itm: isDark ? 'rgba(122,167,255,0.07)' : 'rgba(29,78,216,0.05)',
    hover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    call: C.callText,
    put: C.putText,
    atmBg: C.atmBg,
  }), [C, isDark, theme]);
}
export type WsColors = ReturnType<typeof useWsColors>;

/** Premium / price (native USD) — Delta-style precision. */
export function fmtPx(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (a >= 100) return v.toFixed(1);
  if (a >= 1) return v.toFixed(1);
  return v.toFixed(2);
}

export const fmtStrike = (k: number) => k.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Money (USD in) shown in the chosen currency. ∞ for unbounded. */
export function fmtMoney(usd: number | null | undefined, ccy: Ccy, opts: { sign?: boolean } = {}): string {
  if (usd == null || Number.isNaN(usd)) return '—';
  if (usd === Infinity) return 'Unlimited';
  if (usd === -Infinity) return 'Unlimited';
  const v = ccy === 'INR' ? usd * USD_TO_INR : usd;
  const a = Math.abs(v);
  const digits = ccy === 'INR' ? (a >= 100 ? 0 : 2) : (a >= 100 ? 0 : a >= 1 ? 2 : 3);
  const body = a.toLocaleString(ccy === 'INR' ? 'en-IN' : 'en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const sym = ccy === 'INR' ? '₹' : '$';
  const sign = v < 0 ? '−' : opts.sign && v > 0 ? '+' : '';
  return `${sign}${sym}${body}`;
}

/** Compact number for greeks (keeps tiny values legible). */
export function fmtNum(v: number | null | undefined, digits = 3): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a < 1e-3) return v.toExponential(1);
  return v.toFixed(a >= 100 ? 0 : a >= 10 ? 1 : digits);
}

/** "16h" / "1d" / "26d" until settlement. */
export function timeLeft(settleMs: number, now = Date.now()): string {
  const ms = settleMs - now;
  if (ms <= 0) return 'settled';
  const h = ms / 3600e3;
  if (h < 1) return `${Math.max(1, Math.round(ms / 60e3))}m`;
  if (h < 24) return `${Math.floor(h)}h`;
  return `${Math.round(h / 24)}d`;
}

/** "01 NOV" from YYYY-MM-DD. */
export function expLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short' }).toUpperCase();
}

export const PREFS_KEY = 'charts_optionsWorkspace_v1';
export interface WsPrefs {
  view: 'workspace' | 'classic';
  chartOpen: boolean;
  chartHeight: number;
  chainSide: 'both' | 'CE' | 'PE';
  chainCols: 'iv' | 'greeks' | 'none';
  range: number;          // ±N strikes around ATM; 0 = all
  chainCollapsed: boolean;
  analyticsCollapsed: boolean;
  tab: 'payoff' | 'table' | 'heatmap' | 'greeks';
  ccy: Ccy;
  showProb: boolean;
  heatUnit: 'money' | 'pct';
  /** Fee model: % of notional per side (Delta India options taker; capped at 3.5% of premium, +18% GST). */
  feeRatePct: number;
  /** Fixed ₹ exit defaults, as % of the entry credit. */
  tpPctOfCredit: number;
  slPctOfCredit: number;
  /** Premium-ratio ("3× rule") threshold. */
  ratioX: number;
  /** How MKT legs are priced in the analysis: 'touch' = what a market order gets at the top
   *  of the book (sell at the bid, buy at the ask); 'mark' = Delta's mark (no slippage). */
  fillModel: 'touch' | 'mark';
  /** Draw open-interest walls behind the payoff graph. */
  showOi: boolean;
  /** The chain / positions / payoff panels are folded away: the chart strip takes the height. */
  lowerCollapsed: boolean;
}
export const DEFAULT_PREFS: WsPrefs = {
  view: 'workspace', chartOpen: true, chartHeight: 240, chainSide: 'both', chainCols: 'iv', range: 10,
  chainCollapsed: false, analyticsCollapsed: false, tab: 'payoff', ccy: 'INR', showProb: false, heatUnit: 'money',
  feeRatePct: 0.01, tpPctOfCredit: 70, slPctOfCredit: 100, ratioX: 3, fillModel: 'touch', showOi: true,
  lowerCollapsed: false,
};
export function readPrefs(): WsPrefs {
  try {
    const p = { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') || {}) };
    // 2026-11-03: the old 0.03% default was 3× Delta's real option fee (fills show 0.01%) —
    // a saved copy of the OLD default becomes the new one; a rate typed by hand is kept.
    if (p.feeRatePct === 0.03) p.feeRatePct = 0.01;
    return p;
  } catch { return DEFAULT_PREFS; }
}
export function writePrefs(p: WsPrefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* storage blocked — prefs are a convenience */ }
}

/** 1000 → "1k", 1500 → "1.5k" — chain badges must fit beside the strike. */
export const compactLots = (n: number) => (n >= 1000 ? `${+(n / 1000).toFixed(n % 1000 ? 1 : 0)}k` : String(n));
